import { FileContent } from 'components/common/types';
import { FRAME_LIST_LABELS } from 'utils/frameList';

export interface FrameTable {
  headers: string[];
  rows: Map<string, string>[];
}

interface ExtractOptions {
  requiredHeaders?: string[];
  missingHeaderMessage?: (label: string) => string;
}

export const FrameTableAdapter = {
  fromRows: (rows: string[][]): FrameTable => {
    const [headers = [], ...dataRows] = rows;

    return {
      headers,
      rows: dataRows.map((row) => new Map(headers.map((label, index) => [label, row[index]]))),
    };
  },

  fromRecords: (rows: Record<string, string>[]): FrameTable => ({
    headers: [...new Set(rows.flatMap((row) => Object.keys(row)))],
    rows: rows.map((row) => new Map(Object.entries(row))),
  }),
};

const normalizeHeader = (header: string): string => header.trim().toLowerCase();

const findHeader = (headers: string[], label: string): string | undefined =>
  headers.find((header) => normalizeHeader(header) === normalizeHeader(label));

const assertRequiredHeaders = ({
  headers,
  requiredHeaders,
  missingHeaderMessage,
}: ExtractOptions & { headers: string[] }): void => {
  const missingHeader = requiredHeaders?.find((label) => !findHeader(headers, label));
  if (missingHeader) {
    throw new Error(
      missingHeaderMessage?.(missingHeader) ?? `The file must contain a "${missingHeader}" header column`,
    );
  }
};

const getInclude = (row: Map<string, string>, includeKey?: string): boolean => {
  const value = includeKey ? row.get(includeKey) : undefined;

  return value === undefined || String(value).toLowerCase() !== 'false';
};

const assertResellerColumnOrder = (
  headers: string[],
  isReseller: boolean,
  frameIdKey?: string,
  mediaOwnerKey?: string,
): void => {
  if (!isReseller) return;

  const frameIdIndex = frameIdKey ? headers.indexOf(frameIdKey) : -1;
  const mediaOwnerIndex = mediaOwnerKey ? headers.indexOf(mediaOwnerKey) : -1;

  if (mediaOwnerIndex !== 0 || frameIdIndex !== 1) {
    throw new Error('The file must contain a "Frame ID" column after the "Media owner" column');
  }
};

export const extractFrameCodes = (
  table: FrameTable,
  marketId = '',
  isReseller = false,
  { requiredHeaders = [FRAME_LIST_LABELS.frameId], missingHeaderMessage }: ExtractOptions = {},
): FileContent[] => {
  assertRequiredHeaders({ headers: table.headers, requiredHeaders, missingHeaderMessage });

  const frameIdKey = findHeader(table.headers, FRAME_LIST_LABELS.frameId);
  const mediaOwnerKey = findHeader(table.headers, FRAME_LIST_LABELS.mediaOwner);
  const includeKey = findHeader(table.headers, FRAME_LIST_LABELS.include);

  assertResellerColumnOrder(table.headers, isReseller, frameIdKey, mediaOwnerKey);

  return table.rows.reduce((acc: FileContent[], row) => {
    const frameIdValue = frameIdKey ? row.get(frameIdKey) : undefined;
    if (frameIdValue == null || frameIdValue === '') return acc;

    const frameId = String(frameIdValue);
    const mediaOwner = mediaOwnerKey ? row.get(mediaOwnerKey) : undefined;

    acc.push({
      include: getInclude(row, includeKey),
      code: frameId,
      name: frameId,
      mediaOwner: isReseller && mediaOwner ? mediaOwner : marketId,
    });

    return acc;
  }, []);
};
